'use client';

import { pipe, tap } from 'ramda';

import type { AuthBanner } from '@/api/cms/authenticationBanners';
import type { CMSBonus } from '@/api/cms/bonuses';
import type { RegistrationSlide } from '@/api/cms/slider';
import { parseFiltersFromQS } from '@/helpers/common';
import { decryptWithParse } from '@/helpers/encryption';
import { STORAGE, useLocalStorageStore } from '@/store';

const CUSTOM_OFFER_QUERY_STRING_PARAMETER = 'p';
const REACT_APP_QUERY_STRING_KEY = process.env.REACT_APP_QUERY_STRING_KEY || '';

export type Offer = {
  offerLine1: string;
  offerLine2: string;
};

export type CustomBonus = Pick<
  CMSBonus,
  | 'order'
  | 'title'
  | 'promoText'
  | 'image'
  | 'buttonType'
  | 'buttonLink'
  | 'termsAndConditions'
  | 'identifier'
>;

export type CustomOffer = {
  registrationSlide: Partial<RegistrationSlide>;
  registrationBanner: Partial<AuthBanner> & { code?: string };
  bonuses?: CustomBonus[];
  ttl: number;
};

type CustomOfferStore = CustomOffer & {
  createAt: number;
};

const getCustomOfferFromLocalStorage = () => {
  if (typeof window === 'undefined') return;

  const checkTTL = (customOffer: CustomOfferStore) =>
    new Date().getTime() - customOffer.createAt >= customOffer.ttl
      ? null
      : customOffer;

  const customOfferStore = useLocalStorageStore
    ?.getState()
    ?.get<CustomOfferStore | null>(STORAGE.CUSTOM_OFFER_LOCAL_STORAGE_KEY);

  if (customOfferStore) {
    const checkedOffer = checkTTL(customOfferStore);
    return checkedOffer || undefined;
  }

  return;
};

const getCustomOffers = (queryParam: string, decryptKey: string) => {
  const getQueryString = () =>
    parseFiltersFromQS(window.location.search, [queryParam]);

  const checkQueryParameters =
    (queryParam: string) =>
    (params: Partial<{ [x: string]: string | undefined }>) =>
      params[queryParam] !== undefined ? params[queryParam] : null;

  const decryptParameters = (decryptKey: string) => (cipherParams: string) =>
    cipherParams
      ? decryptWithParse<CustomOffer>(cipherParams, decryptKey)
      : null;

  const saveParametersToLocalStorage = tap(
    (customOffer: CustomOffer | null) => {
      if (customOffer) {
        useLocalStorageStore
          ?.getState()
          ?.replace(STORAGE.CUSTOM_OFFER_LOCAL_STORAGE_KEY, {
            ...customOffer,
            createAt: new Date().getTime(),
          });
      }
    }
  );

  const setOffersFromLocalStorage = () =>
    getCustomOfferFromLocalStorage() || null;

  return pipe(getQueryString, (queryString) => {
    if (!queryString) return null;

    const params = checkQueryParameters(queryParam)(queryString);
    const customOffer = params ? decryptParameters(decryptKey)(params) : null;

    if (customOffer) {
      saveParametersToLocalStorage(customOffer);
    } else {
      setOffersFromLocalStorage();
    }
  })();
};

export const getCustomOffer = (): CustomOffer | undefined | null => {
  if (typeof window === 'undefined') return;

  return getCustomOffers(
    CUSTOM_OFFER_QUERY_STRING_PARAMETER,
    REACT_APP_QUERY_STRING_KEY
  );
};
